// AboutArteknosModal - Manifeste ARTEKNOS × NSIC
// Affiché : (1) au 1er chargement après inscription / migration, (2) sur clic du bouton "(i) Qui sommes-nous ?"
// Style Cargo (high contrast) + titres bleu NSIC #003594

import { useEffect, useRef } from "react";
import { X, Building2, Target, Eye, ShieldCheck, BarChart3, Sparkles, Server, Zap } from "lucide-react";

const NSIC_BLUE = "#003594";

const ADVANTAGES = [
  {
    Icon: Eye,
    title: "Visibilité & Traçabilité totales",
    text: "Maîtrise complète du flux logistique, du suivi GPS temps réel jusqu'à la confirmation de réception. Transparence absolue pour toutes les parties prenantes.",
  },
  {
    Icon: BarChart3,
    title: "Performance industrielle",
    text: "Outils de gestion de stocks et tableaux de bord de ventes (KPI) qui permettent aux partenaires de piloter leur activité avec des données précises.",
  },
  {
    Icon: Sparkles,
    title: "Expérience d'excellence",
    text: "Interface épurée, performante et conçue pour être accessible en toutes circonstances. Adoption rapide par les artisans et les livreurs.",
  },
  {
    Icon: Server,
    title: "Sécurité & Fiabilité",
    text: "Architecture robuste déployée sur Kubernetes — continuité de service et sécurité optimale des transactions garanties.",
  },
];

export default function AboutArteknosModal({ open, onClose, ctaLabel = "Commencer l'expérience" }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    // bloque le scroll body
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in"
      onClick={onClose}
      data-testid="about-arteknos-modal-backdrop"
    >
      <div
        ref={dialogRef}
        className="relative bg-[#0F172A] border border-white/10 rounded-[2rem] md:rounded-[2.5rem] w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
        data-testid="about-arteknos-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-arteknos-title"
      >
        {/* Hero gradient */}
        <div
          className="px-6 md:px-10 py-8 md:py-10 rounded-t-[2rem] md:rounded-t-[2.5rem]"
          style={{
            background: `linear-gradient(135deg, #002F87 0%, ${NSIC_BLUE} 55%, #D4AF37 100%)`,
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 md:top-5 md:right-5 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-colors"
            data-testid="about-modal-close-btn"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <Building2 size={28} className="text-white" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-white/80">
              ARTEKNOS × NSIC
            </span>
          </div>

          <h2
            id="about-arteknos-title"
            className="text-2xl md:text-4xl font-black italic tracking-tight text-white leading-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Bienvenue sur ARTEKNOS
          </h2>
          <p
            className="text-base md:text-lg font-bold text-white/95 mt-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            L'Accélérateur de l'Écosystème Industriel et Commercial au Sénégal
          </p>
        </div>

        {/* CONTENT */}
        <div className="px-6 md:px-10 py-8 md:py-10 space-y-9 text-white">
          {/* QUI SOMMES-NOUS */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={18} style={{ color: NSIC_BLUE }} />
              <h3
                className="text-lg md:text-xl font-black uppercase tracking-tight"
                style={{ color: NSIC_BLUE, fontFamily: "Outfit, sans-serif" }}
              >
                Qui sommes-nous ?
              </h3>
            </div>
            <p className="text-sm md:text-base leading-relaxed text-white/90">
              <strong className="text-white">ARTEKNOS</strong> est une plateforme technologique
              intégrée, conçue pour transformer la logistique et la distribution au Sénégal.
              Nous ne sommes pas une simple application de livraison, mais le socle numérique
              qui relie les artisans locaux, les PME et les grands distributeurs à un
              écosystème de mobilité efficace et transparente.
            </p>
          </section>

          {/* MISSION */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Target size={18} style={{ color: NSIC_BLUE }} />
              <h3
                className="text-lg md:text-xl font-black uppercase tracking-tight"
                style={{ color: NSIC_BLUE, fontFamily: "Outfit, sans-serif" }}
              >
                Notre Mission
              </h3>
            </div>
            <p className="text-sm md:text-base leading-relaxed text-white/90">
              Digitaliser la chaîne de valeur industrielle pour garantir que chaque produit, de
              l'atelier du créateur jusqu'au client final, bénéficie d'une logistique de
              précision. Nous levons les freins technologiques qui limitent la croissance des
              acteurs du commerce local grâce à une solution robuste, évolutive et adaptée aux
              réalités du terrain.
            </p>
          </section>

          {/* CE QUE NOUS APPORTONS */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <ShieldCheck size={18} style={{ color: NSIC_BLUE }} />
              <h3
                className="text-lg md:text-xl font-black uppercase tracking-tight"
                style={{ color: NSIC_BLUE, fontFamily: "Outfit, sans-serif" }}
              >
                Ce que nous apportons à nos partenaires
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ADVANTAGES.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 hover:border-[#D4AF37]/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${NSIC_BLUE}33` }}
                    >
                      <Icon size={16} className="text-[#D4AF37]" />
                    </div>
                    <h4 className="font-black text-sm uppercase tracking-tight text-white">
                      {title}
                    </h4>
                  </div>
                  <p className="text-xs md:text-sm leading-relaxed text-white/80">{text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* VISION */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Zap size={18} style={{ color: NSIC_BLUE }} />
              <h3
                className="text-lg md:text-xl font-black uppercase tracking-tight"
                style={{ color: NSIC_BLUE, fontFamily: "Outfit, sans-serif" }}
              >
                Notre Vision
              </h3>
            </div>
            <p className="text-sm md:text-base leading-relaxed text-white/90">
              Chez ARTEKNOS, nous croyons que la technologie est le levier indispensable pour
              structurer le commerce de demain au Sénégal. En alliant l'artisanat traditionnel
              à l'efficacité de l'industrie numérique, nous construisons les ponts qui
              permettront aux entreprises de demain de croître, de se moderniser et de
              conquérir de nouveaux marchés avec une confiance renouvelée.
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="sticky bottom-0 left-0 right-0 px-6 md:px-10 py-5 bg-[#0F172A] border-t border-white/10 rounded-b-[2rem] md:rounded-b-[2.5rem]">
          <button
            onClick={onClose}
            className="w-full bg-[#003594] py-4 md:py-5 rounded-2xl text-white font-black text-sm md:text-base uppercase tracking-[0.3em] shadow-lg active:scale-95 transition-transform"
            style={{ backgroundColor: NSIC_BLUE }}
            data-testid="about-modal-cta"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
